import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Filter,
  Loader2,
  RefreshCw,
  RotateCcw,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { DateRangePicker } from "@/components/ui/date-range-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { ColumnSettingsMenu } from "@/components/common/ColumnSettingsMenu";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { usePermissions } from "@/hooks/usePermissions";
import {
  useDeleteResourceReinstall,
  useResourceReinstalls,
} from "@/hooks/useResourceQueries";
import { formatDateRangeForAPI } from "@/lib/date-utils";
import { getPaginationItems } from "@/lib/pagination";
import { cn, formatDate, formatDateTime } from "@/lib/utils";
import {
  RESOURCE_REINSTALL_COLUMNS,
  useResourceReinstallColumnsStore,
  type ResourceReinstallColumnId,
} from "@/stores/resourceReinstallColumnsStore";
import { useResourceReinstallFiltersStore } from "@/stores/resourceReinstallFiltersStore";
import {
  VM_PLAN_TYPES,
  type ActionStatus,
  type ResourceReinstallListFilters,
  type ResourceReinstallListItem,
  type VmPlanType,
} from "@/types/admin";

/** Rendered in place of a value the row does not carry. */
const EMPTY = "—";

/**
 * Sized to what each cell carries: `vm` and `tenant` fit a 36-character GUID under the name,
 * `requestedBy` a full e-mail. A `Record` over the id union, so a column added without a width is
 * a compile error.
 */
const COLUMN_WIDTHS: Record<ResourceReinstallColumnId, string> = {
  index: "w-14",
  vm: "w-80",
  ip: "w-36",
  plan: "w-28",
  status: "w-32",
  requestedBy: "w-72",
  tenant: "w-72",
  requestedAt: "w-40",
  finishedAt: "w-40",
  actions: "w-16",
};

const CHIP = "rounded-full px-2 py-0.5 text-[11px] font-medium shadow-none";
const NEUTRAL_CHIP = "border-hairline-strong bg-panel-2 text-ink-3";

/** A reinstall is only ever InProgress, Completed or Failed; any other status falls back to neutral. */
const STATUS_CHIPS: Partial<
  Record<ActionStatus, { label: string; chip: string }>
> = {
  InProgress: {
    label: "In Progress",
    chip: "border-warn/30 bg-warn-soft text-warn-ink",
  },
  Completed: {
    label: "Completed",
    chip: "border-ok/30 bg-ok-soft text-ok-ink",
  },
  Failed: {
    label: "Failed",
    chip: "border-destructive/30 bg-destructive-soft text-destructive-ink",
  },
};

const PLAN_CHIPS: Record<VmPlanType, string> = {
  Trial: "border-info/30 bg-info-soft text-info-ink",
  Paid: "border-ok/30 bg-ok-soft text-ok-ink",
};

/**
 * The VM-reinstall report: every in-place reinstall across tenants, as recorded when it was
 * requested. These are the rows the reinstall limit counts — the Completed ones, whoever started
 * them — so a SuperAdmin removing a row here gives that reinstall back to the customer.
 */
export function ResourceReinstallsPage() {
  const { search, vmPlan, dateRange } = useResourceReinstallFiltersStore(
    (state) => state.filters,
  );
  const page = useResourceReinstallFiltersStore((state) => state.page);
  const pageSize = useResourceReinstallFiltersStore((state) => state.pageSize);
  const setFilter = useResourceReinstallFiltersStore(
    (state) => state.setFilter,
  );
  const setPage = useResourceReinstallFiltersStore((state) => state.setPage);
  const setPageSize = useResourceReinstallFiltersStore(
    (state) => state.setPageSize,
  );
  const resetStoreFilters = useResourceReinstallFiltersStore(
    (state) => state.resetFilters,
  );
  const hasActiveFilters = useResourceReinstallFiltersStore((state) =>
    state.hasActiveFilters(),
  );

  // Subscribed to `hidden`, the value that changes; the store's own `isVisible` is a stable
  // function and would never re-render this table on a toggle.
  const hiddenColumns = useResourceReinstallColumnsStore(
    (state) => state.hidden,
  );
  const toggleColumn = useResourceReinstallColumnsStore(
    (state) => state.toggle,
  );
  const showAllColumns = useResourceReinstallColumnsStore(
    (state) => state.showAll,
  );
  const isVisible = (id: ResourceReinstallColumnId) =>
    !hiddenColumns.includes(id);

  const { isSuperAdmin } = usePermissions();
  const canDelete = isSuperAdmin();

  // The input holds what is being typed; the store holds what the query runs with.
  const [searchInput, setSearchInput] = useState(search);
  const [rowToDelete, setRowToDelete] =
    useState<ResourceReinstallListItem | null>(null);

  // 750ms, the one debounce every admin list uses.
  useEffect(() => {
    if (searchInput === search) return;

    const timer = setTimeout(() => setFilter("search", searchInput), 750);
    return () => clearTimeout(timer);
  }, [searchInput, search, setFilter]);

  const filters: ResourceReinstallListFilters = useMemo(() => {
    const { fromDate, toDate } =
      dateRange.from && dateRange.to
        ? formatDateRangeForAPI(dateRange.from, dateRange.to, false)
        : { fromDate: undefined, toDate: undefined };

    return {
      search: search || undefined,
      vmPlan: vmPlan === "All" ? undefined : vmPlan,
      requestedFrom: fromDate,
      requestedTo: toDate,
      page,
      pageSize,
    };
  }, [search, vmPlan, dateRange, page, pageSize]);

  const {
    data: response,
    isFetching,
    error,
    refetch,
  } = useResourceReinstalls(filters);
  const deleteReinstall = useDeleteResourceReinstall();

  // Typed but not committed yet: nothing is fetching and the table still shows the last result.
  const isSearchPending = searchInput !== search;

  const rows = response?.data ?? [];
  const isReloading = isFetching && rows.length === 0;
  const totalPages = response?.pagination?.totalPages ?? 1;
  const totalCount = response?.pagination?.totalItems ?? 0;

  // The search box keeps its own copy of the term, so clearing the store alone would leave it.
  const resetFilters = () => {
    setSearchInput("");
    resetStoreFilters();
  };

  const handleRefresh = () => {
    if (hasActiveFilters) {
      resetFilters();
    } else {
      void refetch();
    }
  };

  const confirmDelete = () => {
    if (!rowToDelete) return;

    // Closed on either outcome: the toast reports it, and the list is re-read by the hook.
    deleteReinstall.mutate(rowToDelete.id, {
      onSettled: () => setRowToDelete(null),
    });
  };

  // Takes the screen over only with nothing to show; with rows in hand a failure is a banner.
  if (error && !response) {
    return (
      <div className="space-y-4">
        <Card>
          <CardContent className="pt-4">
            <div className="text-center">
              <p className="text-destructive-ink">
                Failed to load VM reinstalls
              </p>
              <Button
                onClick={() => refetch()}
                className="mt-4"
                disabled={isFetching}
              >
                {isFetching && <Spinner className="mr-2" />}
                Retry
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col space-y-2">
      <Card className="relative flex min-h-0 flex-1 flex-col shadow-none">
        {isFetching && !isReloading && (
          <div className="pointer-events-none absolute inset-0 z-20 flex items-center justify-center">
            <div className="flex items-center gap-2 rounded-lg border bg-background px-4 py-2 shadow-lg">
              <RefreshCw className="h-4 w-4 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Updating...</span>
            </div>
          </div>
        )}

        <CardHeader className="shrink-0 p-2">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-3 sm:flex-nowrap sm:items-end">
            <div className="w-full sm:w-[360px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                {isSearchPending && (
                  <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />
                )}
                <Input
                  placeholder="Search VM name, UUID, ID, IP, email, phone"
                  value={searchInput}
                  onChange={(event) => setSearchInput(event.target.value)}
                  // React withholds onChange while an IME composition is open, so a Vietnamese
                  // Telex/VNI term would only reach state once a space closed it.
                  onCompositionEnd={(event) =>
                    setSearchInput(event.currentTarget.value)
                  }
                  className="pl-10 pr-9"
                />
              </div>
            </div>

            <div className="order-3 flex w-full min-w-0 items-center justify-start gap-2 overflow-x-auto sm:order-none sm:w-auto sm:flex-1 sm:self-center sm:justify-end">
              {search && (
                <FilterChip
                  label={`Search: ${search}`}
                  onClear={() => {
                    setSearchInput("");
                    setFilter("search", "");
                  }}
                />
              )}
              {vmPlan !== "All" && (
                <FilterChip
                  label={`Plan: ${vmPlan}`}
                  onClear={() => setFilter("vmPlan", "All")}
                />
              )}
              {dateRange.from && dateRange.to && (
                <FilterChip
                  label={`Requested: ${formatDate(dateRange.from)} – ${formatDate(dateRange.to)}`}
                  onClear={() => setFilter("dateRange", {})}
                />
              )}
            </div>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  title="Filters"
                  aria-label="Filters"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <Filter className="h-4 w-4" />
                </Button>
              </PopoverTrigger>
              <PopoverContent align="start" className="w-80 space-y-4 p-4">
                <div className="space-y-2">
                  <Label htmlFor="reinstallPlanFilter">VM Plan</Label>
                  <Select
                    value={vmPlan}
                    onValueChange={(value) =>
                      setFilter("vmPlan", value as VmPlanType | "All")
                    }
                  >
                    <SelectTrigger id="reinstallPlanFilter">
                      <SelectValue placeholder="VM Plan" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="All">Trial and paid</SelectItem>
                      {VM_PLAN_TYPES.map((plan) => (
                        <SelectItem key={plan} value={plan}>
                          {plan}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="reinstallDateFilter">Requested date</Label>
                  <DateRangePicker
                    value={dateRange}
                    onChange={(range) => setFilter("dateRange", range)}
                    placeholder="Select date range"
                    className="w-full"
                  />
                </div>
              </PopoverContent>
            </Popover>

            <ColumnSettingsMenu
              columns={RESOURCE_REINSTALL_COLUMNS}
              isVisible={isVisible}
              toggle={toggleColumn}
              showAll={showAllColumns}
              hiddenCount={hiddenColumns.length}
              className="h-8 min-h-8 px-2 hover:translate-y-0 active:translate-y-0"
            />
          </div>
        </CardHeader>

        <CardContent className="flex min-h-0 flex-1 flex-col p-2 pt-0">
          {error && (
            <div className="mb-2 flex shrink-0 items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive-soft px-3 py-2">
              <p className="text-sm text-destructive-ink">
                Could not refresh the list. Showing the last result that loaded.
              </p>
              <Button
                onClick={() => refetch()}
                variant="outline"
                size="sm"
                disabled={isFetching}
                className="h-7 shrink-0 px-2 text-xs"
              >
                Retry
              </Button>
            </div>
          )}

          {isReloading ? (
            <div className="min-h-0 flex-1 space-y-2 overflow-hidden rounded-md border p-3">
              {Array.from({ length: 8 }, (_, index) => (
                <Skeleton key={index} className="h-11 w-full" />
              ))}
            </div>
          ) : rows.length === 0 ? (
            <div className="flex min-h-0 flex-1 flex-col items-center justify-center py-16 text-center">
              <RotateCcw className="mx-auto mb-4 h-16 w-16 text-muted-foreground" />
              <h3 className="mb-2 text-lg font-semibold">
                {hasActiveFilters ? "No results found" : "No VM reinstalls yet"}
              </h3>
              <p className="mb-6 text-muted-foreground">
                {hasActiveFilters
                  ? "No reinstall matched. Try adjusting or clearing your filters."
                  : "Every reinstall of a VM, by a customer or an operator, will appear here."}
              </p>
              {hasActiveFilters && (
                <Button variant="outline" onClick={resetFilters}>
                  <RefreshCw className="mr-2 h-4 w-4" />
                  Clear filters
                </Button>
              )}
            </div>
          ) : (
            // The scroll sits on this height-bounded box, not on table.tsx's own wrapper, so the
            // horizontal bar stays on screen and the header sticks.
            <div className="scrollbar-thin min-h-0 flex-1 overflow-auto rounded-md border [&>div]:overflow-visible">
              <Table className="table-fixed border-collapse [&_td:not(:last-child)]:border-r [&_th:not(:last-child)]:border-r">
                {/* Generated from the column list, so a hidden column never leaves its <col> behind. */}
                <colgroup>
                  {RESOURCE_REINSTALL_COLUMNS.filter((column) =>
                    isVisible(column.id),
                  ).map((column) => (
                    <col key={column.id} className={COLUMN_WIDTHS[column.id]} />
                  ))}
                </colgroup>
                <TableHeader className="sticky top-0 z-10 bg-card">
                  <TableRow>
                    {RESOURCE_REINSTALL_COLUMNS.filter((column) =>
                      isVisible(column.id),
                    ).map((column) => (
                      <TableHead
                        key={column.id}
                        className={cn(
                          (column.id === "index" || column.id === "actions") &&
                            "text-center",
                        )}
                      >
                        {column.label}
                      </TableHead>
                    ))}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.map((row, index) => (
                    <TableRow key={row.id}>
                      {isVisible("index") && (
                        <TableCell className="text-center text-xs font-medium text-muted-foreground">
                          {(page - 1) * pageSize + index + 1}
                        </TableCell>
                      )}
                      {isVisible("vm") && (
                        <TableCell>
                          <Link
                            to={`/admin/resources/${row.resourceId}`}
                            className="block truncate text-sm font-medium text-primary hover:underline"
                            title={row.vmName}
                          >
                            {row.vmName}
                          </Link>
                          <div
                            className="truncate font-mono text-[11px] text-muted-foreground select-all"
                            title={row.providerVmUuid}
                          >
                            {/* Outside the click-to-select, so a copy takes the UUID alone. */}
                            <span className="select-none">UUID: </span>
                            {row.providerVmUuid}
                          </div>
                        </TableCell>
                      )}
                      {isVisible("ip") && (
                        <TableCell className="font-mono text-xs">
                          {row.ipAddress ?? EMPTY}
                        </TableCell>
                      )}
                      {isVisible("plan") && (
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={cn(CHIP, PLAN_CHIPS[row.vmPlan])}
                          >
                            {row.vmPlan}
                          </Badge>
                        </TableCell>
                      )}
                      {isVisible("status") && (
                        <TableCell>
                          <Badge
                            variant="outline"
                            className={cn(
                              CHIP,
                              STATUS_CHIPS[row.status]?.chip ?? NEUTRAL_CHIP,
                            )}
                          >
                            {STATUS_CHIPS[row.status]?.label ?? row.status}
                          </Badge>
                        </TableCell>
                      )}
                      {isVisible("requestedBy") && (
                        <TableCell>
                          <div
                            className="truncate text-sm"
                            title={row.userEmail}
                          >
                            {row.userEmail}
                          </div>
                          {row.userPhoneNumber && (
                            <div className="truncate text-xs text-muted-foreground">
                              {row.userPhoneNumber}
                            </div>
                          )}
                        </TableCell>
                      )}
                      {isVisible("tenant") && (
                        <TableCell>
                          <div className="truncate font-medium">
                            {row.tenantName ?? EMPTY}
                          </div>
                          <div
                            className="truncate font-mono text-[11px] text-muted-foreground select-all"
                            title={row.tenantId}
                          >
                            {row.tenantId}
                          </div>
                        </TableCell>
                      )}
                      {isVisible("requestedAt") && (
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          {formatDateTime(row.requestedAt)}
                        </TableCell>
                      )}
                      {isVisible("finishedAt") && (
                        <TableCell className="whitespace-nowrap text-xs text-muted-foreground">
                          {row.finishedAt
                            ? formatDateTime(row.finishedAt)
                            : EMPTY}
                        </TableCell>
                      )}
                      {isVisible("actions") && (
                        <TableCell className="text-center">
                          {/* SuperAdmin only, like the endpoint behind it. */}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title="Remove this record"
                              aria-label="Remove this record"
                              onClick={() => setRowToDelete(row)}
                            >
                              <Trash2 className="h-4 w-4 text-destructive-ink" />
                            </Button>
                          )}
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {!isReloading && (
            <div className="mt-auto flex shrink-0 flex-col items-center gap-2 pt-2 sm:flex-row sm:justify-between">
              <p className="text-sm text-muted-foreground">
                Page {page} of {totalPages} ({totalCount} total)
              </p>
              <div className="flex max-w-full flex-wrap items-center justify-center gap-2">
                <Select
                  value={pageSize.toString()}
                  onValueChange={(value) => setPageSize(parseInt(value, 10))}
                >
                  <SelectTrigger className="h-8 w-[120px] text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10 per page</SelectItem>
                    <SelectItem value="20">20 per page</SelectItem>
                    <SelectItem value="50">50 per page</SelectItem>
                    <SelectItem value="100">100 per page</SelectItem>
                  </SelectContent>
                </Select>
                <div className="flex items-center gap-1 overflow-x-auto">
                  <Button
                    onClick={() => setPage(Math.max(1, page - 1))}
                    disabled={page === 1 || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Previous
                  </Button>
                  {getPaginationItems(page, totalPages).map((item, index) =>
                    item === "ellipsis" ? (
                      <span
                        key={`ellipsis-${index}`}
                        className="px-1 text-sm text-muted-foreground"
                      >
                        …
                      </span>
                    ) : (
                      <Button
                        key={item}
                        onClick={() => setPage(item)}
                        disabled={isFetching}
                        variant={page === item ? "default" : "outline"}
                        size="sm"
                        className="min-w-9"
                      >
                        {item}
                      </Button>
                    ),
                  )}
                  <Button
                    onClick={() => setPage(Math.min(totalPages, page + 1))}
                    disabled={page >= totalPages || isFetching}
                    variant="outline"
                    size="sm"
                  >
                    Next
                  </Button>
                </div>

                <Button
                  onClick={handleRefresh}
                  variant="outline"
                  size="sm"
                  disabled={isFetching}
                  title="Reset filters and reload"
                  aria-label="Reset filters and reload"
                  className="h-8 min-h-8 w-8 p-0 hover:translate-y-0 active:translate-y-0"
                >
                  <RefreshCw className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmDialog
        open={rowToDelete !== null}
        onOpenChange={(open) => {
          if (!open) setRowToDelete(null);
        }}
        title="Remove this reinstall record?"
        description={
          rowToDelete
            ? `The reinstall of "${rowToDelete.vmName}" requested on ${formatDateTime(rowToDelete.requestedAt)} leaves this report and is no longer counted against the customer's reinstall limit. The VM itself is not changed.`
            : ""
        }
        confirmText="Remove"
        variant="destructive"
        isPending={deleteReinstall.isPending}
        onConfirm={confirmDelete}
      />
    </div>
  );
}

/** One active-filter chip: shows what is applied and clears just that one. */
function FilterChip({
  label,
  onClear,
}: {
  label: string;
  onClear: () => void;
}) {
  return (
    <Badge
      variant="outline"
      className="shrink-0 gap-1 whitespace-nowrap border-primary/20 bg-primary/10 text-primary"
    >
      {label}
      <button
        type="button"
        onClick={onClear}
        aria-label={`Clear ${label}`}
        className="cursor-pointer rounded-sm hover:text-destructive focus:outline-none"
      >
        <X className="h-3 w-3" />
      </button>
    </Badge>
  );
}
